import React, { useState, useEffect } from "react";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { trustSignals } from "../data/mock";

const TrustSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const testimonialsPerPage = 3;
  const totalSlides = Math.ceil(trustSignals.testimonials.length / testimonialsPerPage);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener("resize", checkMobile);
    
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const getVisibleTestimonials = () => {
    if (isMobile) {
      // Show 1 testimonial at a time on mobile
      return [trustSignals.testimonials[currentSlide % trustSignals.testimonials.length]];
    }
    // Show 3 testimonials at a time on desktop
    const startIndex = currentSlide * testimonialsPerPage;
    return trustSignals.testimonials.slice(startIndex, startIndex + testimonialsPerPage);
  };

  return (
    <section id="trust" className="section-padding bg-gradient-to-br from-gray-50 to-white">
      <div className="container-custom px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#d4af37] font-semibold text-sm uppercase tracking-wider">
            Client Success Stories
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3 mb-6">
            {trustSignals.title}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {trustSignals.subtitle}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {trustSignals.stats.map((stat, index) => (
            <div
              key={index}
              className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-center border border-gray-100"
            >
              <div className="text-4xl md:text-5xl font-bold text-[#1e3a8a] mb-3">
                {stat.value}
              </div>
              <div className="text-gray-600 font-medium leading-tight">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* Testimonials */}
        <div className="relative max-w-6xl mx-auto">
          {/* Desktop: 3 testimonials */}
          {!isMobile && (
            <div className="grid md:grid-cols-3 gap-8">
              {getVisibleTestimonials().map((testimonial, index) => (
                <div
                  key={currentSlide * testimonialsPerPage + index}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 relative"
                >
                  {/* Quote Icon */}
                  <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-secondary rounded-full flex items-center justify-center shadow-lg">
                    <Quote className="w-6 h-6 text-[#1e3a8a]" />
                  </div>

                  {/* Rating */}
                  <div className="flex items-center space-x-1 mb-4 mt-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-[#d4af37] fill-current" />
                    ))}
                  </div>

                  {/* Testimonial Text */}
                  <p className="text-gray-700 leading-relaxed mb-6 italic">
                    "{testimonial.text}"
                  </p>

                  {/* Author */}
                  <div className="pt-4 border-t border-gray-200">
                    <div className="font-bold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.business}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Mobile: 1 testimonial carousel */}
          {isMobile && (
            <div className="overflow-hidden">
              <div className="px-4">
                {getVisibleTestimonials().map((testimonial, index) => (
                  <div
                    key={currentSlide}
                    className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 relative"
                  >
                    {/* Quote Icon */}
                    <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-secondary rounded-full flex items-center justify-center shadow-lg">
                      <Quote className="w-6 h-6 text-[#1e3a8a]" />
                    </div>

                    {/* Rating */}
                    <div className="flex items-center space-x-1 mb-4 mt-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 text-[#d4af37] fill-current" />
                      ))}
                    </div>

                    {/* Testimonial Text */}
                    <p className="text-gray-700 leading-relaxed mb-6 italic">
                      "{testimonial.text}"
                    </p>

                    {/* Author */}
                    <div className="pt-4 border-t border-gray-200">
                      <div className="font-bold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-600">{testimonial.business}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-6 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white transition-all duration-300 z-10 border-2 border-[#1e3a8a]"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-6 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white transition-all duration-300 z-10 border-2 border-[#1e3a8a]"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Dots Indicator */}
          <div className="flex justify-center mt-8 space-x-2">
            {[...Array(isMobile ? trustSignals.testimonials.length : totalSlides)].map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  currentSlide === index ? "bg-[#1e3a8a] w-8" : "bg-gray-300 w-2"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-20 bg-gradient-primary text-white p-12 rounded-2xl">
          <h3 className="text-3xl font-bold text-center mb-8">
            Why Thousands of Businesses Trust Us
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🏆</span>
              </div>
              <h4 className="font-bold text-lg mb-2">USPTO Registered</h4>
              <p className="text-white/90 text-sm">
                Licensed trademark attorneys registered with the United States Patent and Trademark Office
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">⚡</span>
              </div>
              <h4 className="font-bold text-lg mb-2">Fast Processing</h4>
              <p className="text-white/90 text-sm">
                Streamlined process ensures your trademark application is filed quickly and accurately
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">💯</span>
              </div>
              <h4 className="font-bold text-lg mb-2">98% Success Rate</h4>
              <p className="text-white/90 text-sm">
                Our proven track record demonstrates our expertise in trademark registration
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🛡️</span>
              </div>
              <h4 className="font-bold text-lg mb-2">Comprehensive Protection</h4>
              <p className="text-white/90 text-sm">
                Complete trademark services from search to registration and ongoing monitoring
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustSection;
